<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import type { Props } from './types'

const props = defineProps<Props>()

const { public: { googleApiKey } } = useRuntimeConfig()
const { t } = useI18n()
const emit = defineEmits(['openGallery'])

const staticMapSection = ref()
const isSectionVisible = ref(false)
const observer = ref<IntersectionObserver>()

const mapOptions = `&zoom=15&size=1080x150&scale=2&key=${googleApiKey}`
const baseUrlMap = 'https://maps.googleapis.com/maps/api/staticmap?'
const locationUrlMap = `bottom=${props.lat},${props.lng}`
const mapIcon = `&markers=icon:https://lahaus.imgix.net/static/project_lunch_platform/map_icons/location_marker.png%7Cscale:2%7Canchor:bottom%7C${props.lat},${props.lng}`

const fullUrlMap = `${baseUrlMap}${locationUrlMap}${mapOptions}${mapIcon}`

onMounted(() => {
  observer.value = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        isSectionVisible.value = true
      }
    },
    {
      threshold: 0.1
    }
  )

  observer.value.observe(staticMapSection.value)
})

onUnmounted(() => {
  if (observer.value) {
    observer.value?.disconnect()
  }
})
</script>

<script lang="ts">
export default {
  name: 'AppMapStatic'
}
</script>

<template>
  <section
    v-if="lat && lng"
    ref="staticMapSection"
    class="map-container">
    <h3 class="text-22 font-medium lg:text-32">
      {{ t('title') }}
    </h3>

    <label
      v-if="isSectionVisible"
      :for="id"
      class="text-16">
      {{ locationName }}
    </label>

    <button
      v-if="isSectionVisible"
      :id="id"
      :data-lh-id="id"
      :aria-label="t('ariaLabel')"
      class="w-full mt-8 rounded-12 overflow-hidden relative"
      :class="buttonStyles"
      @click="emit('openGallery', 'location')">
      <figure class="pointer-events-none h-full">
        <img
          :src="fullUrlMap"
          :alt="`static map ${locationName}`"
          :class="imgStyles"
          class="w-full object-cover pointer-events-none">
      </figure>

      <p class="px-16 py-4 text-primary-600 font-medium bg-white border border-primary-600 rounded-8 absolute bottom-5 left-5 pointer-events-none">
        {{ t('cta') }}
      </p>
    </button>
  </section>
</template>

<i18n src="./i18n.json" lang="json" />
